import React from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * ImageViewerModal component for viewing images in full size
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Array} props.images - Array of image files/URLs
 * @param {number} props.currentIndex - Current image index
 * @param {Function} props.setCurrentIndex - Function to update current image index
 */
const ImageViewerModal = ({ isOpen, onClose, images = [], currentIndex = 0, setCurrentIndex }) => {
  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];
  
  // Determine if current image is a File object or URL string
  const isFileObject = currentImage instanceof File;
  const isImage = isFileObject 
    ? currentImage?.type?.startsWith('image/')
    : typeof currentImage === 'string' || (currentImage?.src && !currentImage?.type?.startsWith('video/'));
  
  // Get image source - handle both File objects and URL strings
  const getImageSrc = () => {
    if (isFileObject) {
      return URL.createObjectURL(currentImage);
    } else if (typeof currentImage === 'string') {
      return currentImage;
    } else if (currentImage?.src) {
      return currentImage.src;
    }
    return currentImage;
  };

  const imageSrc = getImageSrc();
  const imageAlt = isFileObject 
    ? (currentImage?.name || 'Image preview')
    : (typeof currentImage === 'string' 
      ? `Image ${currentIndex + 1}` 
      : (currentImage?.alt || currentImage?.name || `Image ${currentIndex + 1}`));

  const handlePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    setCurrentIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, currentIndex]);

  return (
    <div
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-viewer-title"
    >
      <div className="relative w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
          aria-label="Close image viewer"
        >
          <X size={24} />
        </button>

        {/* Navigation buttons */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
              aria-label="Next image"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Image container */}
        <div className="max-w-full max-h-full flex items-center justify-center">
          {isImage ? (
            <img
              src={imageSrc}
              alt={imageAlt}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: '90vh' }}
            />
          ) : (
            <video
              src={imageSrc}
              controls
              className="max-w-full max-h-full"
              style={{ maxHeight: '90vh' }}
            />
          )}
        </div>

        {/* Image counter */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} of {images.length}
          </div>
        )}

        {/* Image name */}
        {(isFileObject && currentImage?.name) && (
          <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded text-sm max-w-xs truncate">
            {currentImage.name}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageViewerModal;
